import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ROUTES } from 'app/utils/constants/paths';

import practiceService from '../services/practiceService';
import { MissingAuthTokenError } from '../services/serviceConfig';
import type { GeneratedSession } from '../types/practiceCourses';
import { useAIChatConversationActions } from '../contexts/AIChatConversation/useAIChatConversationActions';
import { useAIChatPracticeSession } from '../contexts/AIChatPracticeSession/useAIChatPracticeSession';
import { useAIChatShellActions } from '../contexts/AIChatShell/useAIChatShellActions';

type UseReviewSessionGenerationResult = {
  isGenerating: boolean;
  hasError: boolean;
  generateReviewSession: (sessionId: string) => void;
  dismissError: () => void;
};

/**
 * Orchestrates the review flow on the practice summary screen: from a completed
 * session it asks the backend to generate a fresh review session and navigates
 * straight into its first activity, mirroring the custom practice flow.
 *
 * Unlike the custom flow (which is launched from a different view and unmounts on
 * navigation), this runs INSIDE ChatbotPracticeView: the summary and the new activity
 * share the same mounted view. The stale practice-session context therefore has to be
 * reset before navigating so the new session's first activity loads cleanly — the same
 * sequence ChatbotPracticeView uses when transitioning between activities.
 */
function useReviewSessionGeneration(): UseReviewSessionGenerationResult {
  const history = useHistory();
  const { setCurrentConversationId, clearMessageQueue } = useAIChatConversationActions();
  const { closeChatbot } = useAIChatShellActions();
  const { resetPracticeSession } = useAIChatPracticeSession();

  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return function () {
      isMountedRef.current = false;
    };
  }, []);

  const navigateToFirstActivity = useCallback(
    (session: GeneratedSession | null): boolean => {
      const firstActivity = session?.activityInstances?.[0];
      if (!session?.id || !firstActivity?.id) return false;

      // Reset the stale (completed) session state before entering the new session,
      // mirroring ChatbotPracticeView's between-activity transition.
      clearMessageQueue();
      closeChatbot();
      setCurrentConversationId(null);
      resetPracticeSession();

      const path = ROUTES.CHATBOT_PRACTICE_SESSION_ACTIVITY.replace(':sessionId', session.id).replace(':activityInstanceId', firstActivity.id);
      history.push(path);
      return true;
    },
    [clearMessageQueue, closeChatbot, setCurrentConversationId, resetPracticeSession, history]
  );

  const generateReviewSession = useCallback(
    async (sessionId: string) => {
      if (!sessionId) return;

      setHasError(false);
      setIsGenerating(true);

      try {
        const session = (await practiceService.generateReviewSession(sessionId)) as GeneratedSession;

        if (!isMountedRef.current) return;

        const navigated = navigateToFirstActivity(session);
        // Navigation happens within the same view, so the dialog must be closed here;
        // if the response had no activity to enter, fall back to the error surface.
        setIsGenerating(false);
        if (!navigated) {
          setHasError(true);
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        setIsGenerating(false);
        // A missing auth token is an expected "not logged in" state, not a real failure.
        if (!(error instanceof MissingAuthTokenError)) {
          setHasError(true);
        }
      }
    },
    [navigateToFirstActivity]
  );

  const dismissError = useCallback(() => {
    setHasError(false);
  }, []);

  return {
    isGenerating,
    hasError,
    generateReviewSession,
    dismissError,
  };
}

export default useReviewSessionGeneration;
